import { Component, EventEmitter, Input, Output, forwardRef, inject, OnInit, OnChanges, } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, } from '@angular/forms';
import { NgSelectComponent, NgOptionTemplateDirective } from '@ng-select/ng-select';
import { ClientsService, ClientListFilters } from '../../../core/services/clients.service';

interface AnyClient { id: number; name: string; humanizedName?: string }

@Component({
  selector: 'app-client-select',
  standalone: true,
  imports: [FormsModule, NgSelectComponent, NgOptionTemplateDirective],
  templateUrl: './client-select.component.html',
  styleUrls: ['./client-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClientSelectComponent),
      multi: true,
    },
  ],
})
export class ClientSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  private clientsService = inject(ClientsService);

  @Input() clients: AnyClient[] | null = null;
  @Input() autoLoad = false;
  @Input() listFilters: ClientListFilters = {};
  @Input() placeholder = 'Clients';
  @Input() disabled = false;
  @Input() clearable = true;
  @Input() name = 'clientId';
  @Input() multiple = true;

  @Input() selectedId: number | null = null;
  @Output() selectedIdChange = new EventEmitter<number | null>();

  @Input() selectedIds: number[] = [];
  @Output() selectedIdsChange = new EventEmitter<number[]>();

  @Output() changed = new EventEmitter<void>();

  model: number | number[] | null = null;

  private onCvaChange: (value: any) => void = () => {};
  private onCvaTouched: () => void = () => {};

  ngOnInit() {
    if (!this.clients && this.autoLoad) {
      this.clientsService.getList(undefined, this.listFilters || {}).subscribe(result => {
        this.clients = result.clients;
      });
    }

    this.syncModelFromInputs();
  }

  ngOnChanges() {
    this.syncModelFromInputs();
  }

  private syncModelFromInputs() {
    if (this.multiple) {
      this.model = Array.isArray(this.selectedIds) ? this.selectedIds : [];
    } else {
      this.model = this.selectedId ?? null;
    }
  }

  // Search matches name OR humanizedName
  clientSearchFn = (term: string, item: AnyClient) => {
    const t = (term || '').toLowerCase().trim();
    if (!t) return true;

    return (
      (item?.name ?? '').toLowerCase().includes(t) ||
        (item?.humanizedName ?? '').toLowerCase().includes(t)
    );
  };

  onModelChange(value: number | number[] | null) {
    this.model = value;

    if (this.multiple) {
      const ids = Array.isArray(value) ? value : [];
      this.selectedIds = ids;
      this.selectedIdsChange.emit(ids);

      this.onCvaChange(ids);
    } else {
      const id = typeof value === 'number' ? value : null;
      this.selectedId = id;
      this.selectedIdChange.emit(id);

      this.onCvaChange(id);
    }

    this.changed.emit();
  }

  onBlur() {
    this.onCvaTouched();
  }

  writeValue(value: any): void {
    if (this.multiple) {
      this.model = Array.isArray(value) ? value : [];
      this.selectedIds = Array.isArray(value) ? value : [];
    } else {
      this.model = typeof value === 'number' ? value : null;
      this.selectedId = typeof value === 'number' ? value : null;
    }
  }

  registerOnChange(fn: any): void {
    this.onCvaChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onCvaTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
